/**
* Licensed Materials - Property of IBM
* IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2018, 2022
* US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
*/
define([
	'underscore',
	'./util/DashboardSpecHelper',
	'../app/nls/StringResources',
	'../app/util/CapabilityUtils'
], function(
	_,
	DashboardSpecHelper,
	StringResources,
	CapabilityUtils
) {
	return class CopyPasteController {
		/**
		 * @classdesc CopPasteController
		 * Used for copying and pasting widgets across different CA dashboards
		 * Copy flow for DB => select widgets from DB spec => store in glass clipboard service in shared spec obj => show toast msgs
		 * Copy flow for siblings => Use this classes doCopy method and provide a overwriteSpec
		 * Paste flow for DB => get data from clipboard service => convert via conversion service => render widget & show toast msgs
		 * Paste flow for children => provide a overwriteTarget to doPaste => accept returned converted spec and render appropriately; doPaste will show toast msgs
		 * @param {Object} options Expects layoutController, dashboardAPI, logger, model, api
		 */
		constructor(options) {
			this.layoutController = options.layoutController;
			this.dashboardApi = options.dashboardApi;
			this.logger = options.logger;
			this.model = options.model;
			this.api = options.api;
			this.specType = options.type || 'DASHBOARD';
			this.clipboard = this.dashboardApi.getGlassCoreSvc('.Clipboard');
		}

		destroy() {
			this.layoutController = null;
			this.dashboardApi = null;
			this.logger = null;
			this.model = null;
			this.api = null;
			this.clipboard = null;
		}

		getSpecHelper() {
			if (this.specHelper) {
				return Promise.resolve(this.specHelper);
			}

			return this.layoutController.getInteractionController()
				.then(controller => {
					this.specHelper = new DashboardSpecHelper(controller);
					return this.specHelper;
				});
		}

		/**
		 * Handles the copy gesture by getting the spec and saving it
		 * @param {Object} overwriteSpec Spec obj with {type: str, spec: json str, count: number}
		 * @return {Promise} Returns a promise which will resolve after glass
		 */
		doCopy(overwriteSpec) {
			return this.getSpecHelper()
				.then(specHelper => {
					let currentTime = (new Date()).getTime(),
						sharedSpecObj;

					const overwriteSpecIsValid = (spec) => spec && spec.type !== 'DASHBOARD' && spec.count;

					// make sure incoming overwritespec fits our shared criteria
					if (overwriteSpecIsValid(overwriteSpec)) {
						sharedSpecObj = overwriteSpec;
					} else {
						sharedSpecObj = this.selectFromSpecForCopyPaste(specHelper);
						sharedSpecObj.type = this.specType;
					}

					sharedSpecObj.timestamp = currentTime; // Set timestamp for Reporting (TODO: verify and remove in the future)
					return this.clipboard.set(sharedSpecObj)
						.then(() => {
							if(sharedSpecObj.errMsg) {
								this._showToast(sharedSpecObj.errMsg);
							}
							if (sharedSpecObj.count > 0) {
								return this._showToast('copy', { count: sharedSpecObj.count });
							}
							return null;
						});
				})
				.catch(err => this._showError('specCopyErr', err));
		}

		/**
		 * Handles the paste gesture by getting the spec from localstorage, validating it then performs the paste
		 * @param {String} overwriteTarget return a converted spec for a different target; Requires a converter registered with the conversion-service with this target
		 * @return {Promise} Returns a promise which will resolve after glass
		 */
		doPaste(overwriteTarget) {
			let pasteTargetId = null; // default to the main canvas
			// Paste can be performed on the main canvas or can be done within another inner tab collection
			const selectedContents = this.dashboardApi.getFeature('Canvas').getSelectedContentList({ type: 'tab' });
			if (selectedContents.length) {
				pasteTargetId = selectedContents[0].getId();
			}

			if (overwriteTarget) {
				return this.getConvertedSpec(overwriteTarget)
					.then(spec => {
						const specJson = spec ? JSON.parse(spec) : undefined;
						if (specJson) {
							return CapabilityUtils.checkAuthoringCapability(this.dashboardApi, specJson, StringResources.get('noCapabilityForPaste'))
								.then((hasCapability) => {
									return hasCapability ? specJson : undefined;
								});
						}
						return specJson;
					})
					.catch(err => this._showError('specConvertErr', err));
			}

			return this.getConvertedSpec(this.specType)
				.then(spec => {
					if (!spec) return;
					return this.getSpecHelper()
						.then(specHelper => specHelper.validateDashboardSpec(spec));
				})
				.then(spec => {
					return CapabilityUtils.checkAuthoringCapability(this.dashboardApi, spec, StringResources.get('noCapabilityForPaste'))
						.then((hasCapability) => {
							return hasCapability ? spec : null;
						});
				})
				.then(spec => {
					if (!spec) return;
					// remove any current selections
					this.layoutController.interactionController.selectionHandler.deselectAll();
					return this._performPaste(spec, pasteTargetId);
				}).catch(err => this._showError('specConvertErr', err));
		}

		/**
		 * Retrieves a converted spec, running through the clipboard and conversion services
		 * @returns {Promise} resolves to return the minimized spec selected widget(s) as a JSON string
		 */
		getConvertedSpec(target) {
			return this.clipboard.get().then(spec => {
				return this._doConvert(spec, target);
			});
		}

		_doConvert(spec, target) {
			var src = spec.type;
			var data = spec.spec;
			if( src == 'REPORT') {
				this._showError('pasteNotSupportedErr', { name: StringResources.get('reportParam') });
				return null;
			}
			return src === target || !data ? Promise.resolve(data) : this.dashboardApi.getGlassSvc('.ConversionService').then(srvc => srvc.convert(src, target, data));
		}

		/**
		 * Selects the widgets to copy
		 * @returns returns the minimized spec selected widget(s) as JSON
		 */
		selectFromSpecForCopyPaste(specHelper) {
			let returnObject = {
				spec: null,
				count: 0
			};

			const selectedContent = this.dashboardApi.getCanvas().getSelectedContentList();
			if (selectedContent.length) {
				returnObject = specHelper.getContentsToJSONSpec();
			}
			return returnObject;
		}

		/**
		 * TODO - look at organizing this better either by creating a separate class, or even consider how the spec fragment is being handled in general.
		 * TODO - Currently, it is being extended on a need to basis, it might be better to copy the full spec and take what is needed based on some configuration file
		 * Does the work of pasting widgets
		 * @param {object} spec			- spec representing what to paste
		 * @param {String} targetLayoutId	- unique identifier of the paste target layout container. (if omitted, the target is considered to be root canvas)
		 * @return {Promise} Returns a resolved promise if all widgets are pasted correctly or there is nothing to paste
		 */
		_performPaste(spec, targetLayoutId) {
			if (spec.nonMergedWidgets) {
				spec.widgets = spec.nonMergedWidgets;
				delete spec.nonMergedWidgets;
			}

			const getLayout = (idx) => idx < spec.layout.length ? spec.layout[idx] : undefined;

			const findObj = (ary, id) =>  _.find(ary, (ele) => id === ele.id);

			function findSource(widget, sourceMap) {
				if (widget.data && widget.data.dataViews) {
					_.each(widget.data.dataViews, (view) => {
						const newSource = _.find(spec.dataSources.sources, source => source.id === view.modelRef);
						sourceMap[view.modelRef] = newSource;
					});
				}
			}

			// finds a widget and associated data source
			function findWidget(id, widgets, sourceMap) {
				let widget = findObj(spec.widgets, id);
				if (widget) {
					// get datasource associated to widget
					findSource(widget, sourceMap);
					widgets.push(widget);
				}
			}

			// recursively finds a widgets and associated data sources
			function findWidgets (items, widgets, sourceMap) {
				items.forEach( item =>  {
					findWidget(item.id, widgets, sourceMap);
					if (item.items) {
						findWidgets(item.items, widgets, sourceMap);
					}
				});
			}

			const getFragment = (layout) => {
				let fragmentModel = {};
				let content = null;
				if (this._isTypeRegistered(layout.type)) {
					content = layout;
				} else {
					fragmentModel.layout = layout;
					// get the widget
					if (spec.widgets && spec.widgets.length) {
						let widgets = [];
						let sourceMap = {};
						// handle grouping
						if (layout.items) {
							findWidgets(layout.items, widgets, sourceMap);
						}
						findWidget(layout.id, widgets, sourceMap);

						if (widgets.length) {
							fragmentModel.widgets = widgets;

							fragmentModel.dataSources = {
								version: spec.dataSources.version,
								sources: _.values(sourceMap)
							};
							// TODO: instead of stripping out a known attribute, we should ignore anything runtime/transient attribute (eg. runtimeOnly flag) in the model.
							// When explicitScale is set to truthy, the VIDA normalization is not set; hence we does not want this property
							if (layout.content && layout.content.properties) {
								const contentProperties = Object.keys(layout.content.properties);
								if (contentProperties.includes('explicitScale')) {
									delete layout.content.properties['explicitScale'];
								}
							}
						}
					}

					if (spec.drillThrough && spec.drillThrough.length) {
						fragmentModel.drillThrough = spec.drillThrough;
					}

					if(spec.properties) {
						fragmentModel.properties = spec.properties;
					}

					if(spec.fredIsRed) {
						fragmentModel.fredIsRed = spec.fredIsRed;
					}

					if(spec.episodes) {
						fragmentModel.episodes = spec.episodes;
					}
				}

				return {
					content,
					model: fragmentModel
				};
			};

			// recursively paste widgets into dashboard (if the dashboard is templated, then the widgets are placed in different slots)
			const apply = (idx, transactionToken) => {
				let layout = getLayout(idx);
				if (layout) {
					let fragment = getFragment(layout);
					fragment.copyPaste = true;

					// if there is a layout then there is a widget to paste
					if (fragment && (fragment.content || (fragment.model && fragment.model.layout))) {
						return this.dashboardApi.getFeature('Canvas').addContent({
							containerId: targetLayoutId,
							spec: fragment.content || fragment.model,
							copyPaste: true
						}, transactionToken).then((content) => {
							let layoutModelId = content.getId();
							// TODO: layoutReady() is the better intended method for the behavior wanted here, it is not working as
							// expected, some investigation is required by Remi
							return this.layoutController.whenWidgetRenderComplete(layoutModelId).then((layout) => {
								// select the pasted widget
								this.layoutController.interactionController.selectionHandler.selectNode(layout.domNode, {
									isTouch: false
								});
							});
						});
					} else {
						return Promise.reject(new Error('Unable to complete pasting of widget(s). Can not determine the layout fragment'));
					}
				} else {
					return Promise.reject(new Error('Unable to complete pasting of widget(s). Checking for a layout that is not there'));
				}
			};

			// in order to build a fragment model per layout, traverse the layout to get its widget(s), then get the datasources for the widgets
			if (spec) {
				if (spec.layout) {
					let promises = [];
					// create a transaction token in order for paste to be undone/redone with one click
					const transaction = this.dashboardApi.getFeature('Transaction');
					const transactionToken = transaction.startTransaction();
					for (var i=0; i< spec.layout.length; i++){
						promises.push(apply(i, transactionToken));
					}
					transaction.endTransaction(transactionToken);
					return Promise.all(promises);
				} else {
					return Promise.resolve();
				}
			} else {
				return Promise.resolve();
			}
		}

		_isTypeRegistered(type) {
			const contentTypeRegistry = this.dashboardApi.getFeature('ContentTypeRegistry');
			return contentTypeRegistry.isTypeRegistered(type);
		}

		// Log error messages and show toast
		_showError(errKey, err) {
			this.logger.error(StringResources.get(errKey), err);
			if (err instanceof Error) {
				// it's not a message parameter, do not pass to show toast. The internal error is already logged on console
				err = null;
			}
			return this._showToast(errKey, err , {
				type: 'error',
				'preventDuplicates': true
			});
		}

		// Show toast popup
		_showToast(strServiceKey, strParams, toastParams) {
			let toastMsg = !strParams ? StringResources.get(strServiceKey) : StringResources.get(strServiceKey, strParams);
			return this.dashboardApi.showToast(toastMsg, toastParams);
		}
	};
});
